import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Loader2,
  PencilLine,
  ExternalLink,
  RefreshCw,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Spinner } from "@/components/ui/Spinner";
import { toDatetimeLocalValue } from "@/utils/datetime";
import { formatDateTime } from "@/utils/formatDate";
import { PageHeader } from "@/features/power_projects/components/ui";
import {
  useStartEmlImport,
  useTaskPolling,
  useCancelTask,
  useClients,
  useZECapabilities,
  useReanalyzeEmlRfx,
} from "../hooks";
import * as api from "../api";
import {
  computeWarnings,
  effectiveScope,
  isConstructionScope,
  isQualified,
  rkwBadgeLabel,
} from "../emlQualification";
import type { EmlImportTaskResult, EmlRfxRow, EmlRfxResult } from "../types";

// ── date helper ───────────────────────────────────────────────────────────────
// EML deadline is raw HTML cell text — Polish format "30.06.2026, 09:00:00"
// which JS Date() can't parse. Convert to datetime-local compatible "YYYY-MM-DDTHH:mm".
function parseEmlDeadline(s: string | null | undefined): string {
  if (!s) return "";
  const m = s.match(/(\d{2})\.(\d{2})\.(\d{4})(?:[,\s]+(\d{2}):(\d{2}))?/);
  if (m) {
    const [, dd, mm, yyyy, hh = "00", min = "00"] = m;
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }
  // Fallback dla innych formatów: przez lokalny zegar ścienny, nie surowy UTC
  // (.slice na toISOString gubiłby strefę i pokazywał godzinę o −2 h latem).
  return toDatetimeLocalValue(new Date(s));
}

// ── CONNECT portal link ───────────────────────────────────────────────────────
// Public RfX status page on the ORLEN CONNECT portal (mirrors source_url stored
// on the tender by EmlImportConfirmView).
function connectRfxUrl(rfxId: string): string {
  return `https://connect.orlen.pl/app/outRfx/${rfxId}/supplier/status`;
}

// ── log progress parser ───────────────────────────────────────────────────────
function parseProgress(log: string) {
  const totalMatch = log.match(/Znaleziono (\d+) przetarg/);
  const total = totalMatch ? parseInt(totalMatch[1]) : 0;
  const done = (log.match(/Gotowe\. Pewność:/g) ?? []).length;
  const errors = (log.match(/\] Błąd:/g) ?? []).length;
  return { total, completed: done + errors };
}

// ── helpers ───────────────────────────────────────────────────────────────────
const CONFIDENCE_LABEL: Record<string, string> = {
  confirmed: "Potwierdzona",
  low_confidence: "Niska pewność",
  needs_vision: "Wymaga przeglądu",
  failed: "Nie zlokalizowano",
};
const CONFIDENCE_COLOR: Record<string, string> = {
  confirmed: "text-emerald-600",
  low_confidence: "text-amber-500",
  needs_vision: "text-amber-500",
  failed: "text-red-500",
};
const SCOPE_LABEL: Record<string, string> = {
  projekt: "Projekt",
  wykonawstwo: "Wykonawstwo",
  projekt_i_wykonawstwo: "Projekt i wykonawstwo",
};

// ── task progress view ────────────────────────────────────────────────────────
function TaskProgress({
  log,
  onCancel,
  cancelling,
}: {
  log: string;
  onCancel: () => void;
  cancelling: boolean;
}) {
  const { total, completed } = parseProgress(log);
  const pct = total > 0 ? Math.round((completed / total) * 100) : null;
  const logRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [log]);

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)]">
          <Spinner />
          Pobieranie i analiza dokumentów CONNECT…
        </div>
        <button
          onClick={onCancel}
          disabled={cancelling}
          className="text-xs text-[var(--color-text-tertiary)] hover:text-[var(--color-primary)] disabled:opacity-50 transition-colors"
        >
          {cancelling ? "Anulowanie…" : "Anuluj"}
        </button>
      </div>

      {/* progress bar */}
      {pct !== null && (
        <div className="space-y-1">
          <div className="flex justify-between text-[11px] font-mono text-[var(--color-text-tertiary)]">
            <span>
              {completed} / {total} przeanalizowanych
            </span>
            <span>{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[var(--color-border)] overflow-hidden">
            <div
              className="h-full bg-[var(--color-primary)] transition-all duration-500 rounded-full"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      <pre
        ref={logRef}
        className="text-[11px] font-mono text-[var(--color-text-secondary)] whitespace-pre-wrap leading-relaxed max-h-36 overflow-y-auto bg-[var(--color-bg-elevated)] rounded p-2"
      >
        {log || "Inicjalizacja…"}
      </pre>
    </div>
  );
}

// ── AI summary pill row ───────────────────────────────────────────────────────
function Chip({
  children,
  color,
}: {
  children: React.ReactNode;
  color?: string;
}) {
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded font-mono bg-slate-100 dark:bg-slate-800 ${color ?? "text-[var(--color-text-secondary)]"}`}
    >
      {children}
    </span>
  );
}

function AiRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === "" || value === false) return null;
  return (
    <tr className="border-b border-[var(--color-border-muted)] last:border-0 align-top">
      <td className="py-1 pr-4 text-[10px] font-mono text-[var(--color-text-tertiary)] whitespace-nowrap w-36 shrink-0">
        {label}
      </td>
      <td className="py-1 text-[11px] text-[var(--color-text-primary)] break-words">
        {value}
      </td>
    </tr>
  );
}

function RkwBadges({
  rkw,
  scope,
  warnings,
}: {
  rkw: string[];
  scope: string | null | undefined;
  warnings: string[];
}) {
  const isProj = scope === "projekt" || scope === "projekt_i_wykonawstwo";
  const isBud = scope === "wykonawstwo" || scope === "projekt_i_wykonawstwo";
  const missingProj = new Set(
    warnings
      .filter((w) => w.includes("projektowych"))
      .map((w) => w.replace("Brak uprawnień projektowych: ", "")),
  );
  const missingBud = new Set(
    warnings
      .filter((w) => w.includes("wykonawczych"))
      .map((w) => w.replace("Brak uprawnień wykonawczych: ", "")),
  );

  // Green only when fully cleared (no warnings at all), matching the
  // qualifiedRows filter behind "Dodaj wszystkie z uprawnieniami".
  const allOk = warnings.length === 0;

  return (
    <div className="space-y-1">
      <div
        className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded ${allOk ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"}`}
      >
        {rkwBadgeLabel(warnings)}
      </div>
      <div className="flex flex-wrap gap-1 mt-1">
        {rkw.map((r) => {
          const projMissing = isProj && missingProj.has(r);
          const budMissing = isBud && missingBud.has(r);
          const missing = projMissing || budMissing;
          return (
            <span
              key={r}
              className={`text-[10px] px-1.5 py-0.5 rounded font-mono flex items-center gap-0.5 ${
                missing
                  ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
                  : "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
              }`}
            >
              {missing ? "✗" : "✓"} {r}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function AiSummary({
  result,
  warnings,
}: {
  result: EmlRfxResult;
  warnings: string[];
}) {
  const sig = result.signals;
  const parcel = result.parcels?.[0];

  return (
    <div className="mt-2 border-t border-[var(--color-border-muted)] pt-2">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Sparkles size={11} className="text-violet-500" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--color-text-tertiary)] italic">
          Analiza AI
        </span>
      </div>
      <table className="w-full border-collapse">
        <tbody>
          <AiRow label="Typ sieci" value={sig?.network_type} />
          {/* Zakres z prefiksu nazwy, a dopiero w jego braku z analizy AI — ta sama
              kolejność, którą stosuje backend, więc wiersz pokazuje dokładnie ten
              zakres, na podstawie którego przetarg wejdzie (albo nie) do ZE. */}
          <AiRow
            label="Zakres"
            value={
              effectiveScope(result)
                ? (SCOPE_LABEL[effectiveScope(result)] ?? effectiveScope(result))
                : null
            }
          />
          <AiRow label="Rejon" value={sig?.energa_rejon} />
          <AiRow
            label="Dł. kabla"
            value={
              sig?.cable_length_m != null
                ? `${sig.cable_length_m.toLocaleString("pl-PL")} m`
                : null
            }
          />
          <AiRow
            label="Dł. demontażu"
            value={
              sig?.demolished_length_m != null
                ? `${sig.demolished_length_m.toLocaleString("pl-PL")} m`
                : null
            }
          />
          <AiRow
            label="Miejscowość"
            value={
              sig?.locality
                ? `${sig.locality}${sig.gmina ? `, gm. ${sig.gmina}` : ""}`
                : null
            }
          />
          {(sig?.rkw_required?.length ?? 0) > 0 && (
            <AiRow
              label="RKW wymagane"
              value={
                <RkwBadges
                  rkw={sig!.rkw_required}
                  scope={sig!.contract_scope}
                  warnings={warnings}
                />
              }
            />
          )}
          {parcel && (
            <AiRow
              label="Działka"
              value={
                <span
                  className={`font-mono ${CONFIDENCE_COLOR[parcel.confidence] ?? ""}`}
                >
                  {parcel.teryt}
                  {parcel.obreb ? ` · ${parcel.obreb}` : ""}
                  {" · "}
                  {CONFIDENCE_LABEL[parcel.confidence] ?? parcel.confidence}
                </span>
              }
            />
          )}
          {sig?.reasoning && (
            <AiRow
              label="Uzasadnienie"
              value={
                <em className="text-[var(--color-text-secondary)] not-italic leading-relaxed">
                  {sig.reasoning}
                </em>
              }
            />
          )}
        </tbody>
      </table>
      {warnings.length > 0 && (
        <div className="mt-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-2 space-y-0.5">
          {warnings.map((w, i) => (
            <p
              key={i}
              className="text-[11px] text-amber-700 dark:text-amber-400 flex gap-1.5"
            >
              <span className="shrink-0">⚠</span>
              {w}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

// ── per-item status ───────────────────────────────────────────────────────────
type ItemAddStatus = "idle" | "adding" | "done" | "error";

interface ItemState {
  title: string;
  editingTitle: boolean;
  addStatus: ItemAddStatus;
  tenderId?: string;
  addError?: string;
  /** undefined = not yet checked; null = no duplicate; string = existing tender id */
  existingTenderId?: string | null;
}

// ── drop zone ─────────────────────────────────────────────────────────────────
function DropZone({ onFile }: { onFile: (f: File) => void }) {
  return (
    <FileDropZone
      accept=".eml"
      label="Przeciągnij plik .eml lub kliknij, aby wybrać z dysku"
      onSelect={(files) => {
        const f = files[0];
        // Only .eml files start an import; anything else is ignored.
        if (f && f.name.toLowerCase().endsWith(".eml")) onFile(f);
      }}
    />
  );
}

const LS_KEY = "eml_import_task_id";

// ── page ──────────────────────────────────────────────────────────────────────
export function EmlImportPage() {
  const [taskId, setTaskIdState] = useState<string | null>(() =>
    localStorage.getItem(LS_KEY),
  );
  const startImport = useStartEmlImport();

  const setTaskId = (id: string | null) => {
    setTaskIdState(id);
    if (id) localStorage.setItem(LS_KEY, id);
    else localStorage.removeItem(LS_KEY);
  };
  const { data: task } = useTaskPolling(taskId);
  const cancelTask = useCancelTask(taskId);
  const { data: clients = [] } = useClients();
  const { data: capabilities } = useZECapabilities();

  const isRunning =
    !!taskId &&
    !!task?.status &&
    !["success", "failure", "cancelled"].includes(task.status);
  const isDone = task?.status === "success";
  const isFailed = task?.status === "failure" || task?.status === "cancelled";

  // Read partial results even while running — backend saves after every item. Failed and
  // cancelled imports keep their per-item partials too (the task persists after every
  // tender), so an interrupted import still shows everything analysed so far instead of
  // discarding it behind a bare "Analiza nie powiodła się".
  const taskResult =
    task?.result && (isRunning || isDone || isFailed)
      ? (task.result as EmlImportTaskResult)
      : null;
  const rfxRows: EmlRfxRow[] = taskResult?.rfx_rows ?? [];
  const results: Record<string, EmlRfxResult> = taskResult?.results ?? {};

  // global client selector
  const [globalClient, setGlobalClient] = useState("");

  // RfX rows the user dismissed from the import list (local only — no tender is
  // created and the backend result is left untouched).
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const dismissRow = (rfx: string) =>
    setDismissed((prev) => new Set(prev).add(rfx));

  // per-item state — merge new rows as they arrive (works during running + done)
  const [items, setItems] = useState<Record<string, ItemState>>({});

  useEffect(() => {
    if (rfxRows.length === 0) return;
    setItems((prev) => {
      const next = { ...prev };
      rfxRows.forEach((row) => {
        const rfxResult = results[row.rfx];
        // Prefer full title from CONNECT page or AI extraction over truncated EML name
        const bestTitle =
          rfxResult?.connect_title ||
          rfxResult?.signals?.project_name ||
          row.nazwa ||
          "";
        if (!next[row.rfx]) {
          next[row.rfx] = {
            title: bestTitle,
            editingTitle: false,
            addStatus: "idle",
          };
        } else if (
          !next[row.rfx].editingTitle &&
          next[row.rfx].addStatus === "idle" &&
          bestTitle &&
          bestTitle !== next[row.rfx].title &&
          (rfxResult?.connect_title || rfxResult?.signals?.project_name)
        ) {
          // Upgrade truncated EML title to full title once AI result arrives
          next[row.rfx] = { ...next[row.rfx], title: bestTitle };
        }
      });
      return next;
    });
  }, [rfxRows.length, Object.keys(results).length]);

  // Auto-select Energa client: exact match first, then "energa" substring, then sole client.
  useEffect(() => {
    if (globalClient || clients.length === 0) return;
    const exact = clients.find((c) => /energa.operator/i.test(c.name));
    const partial = clients.find((c) => /energa/i.test(c.name));
    const pick = exact ?? partial ?? (clients.length === 1 ? clients[0] : null);
    if (pick) setGlobalClient(pick.id);
  }, [clients.length]);

  const setItem = (rfx: string, patch: Partial<ItemState>) =>
    setItems((s) => ({ ...s, [rfx]: { ...s[rfx], ...patch } }));

  const checkingRef = useRef(new Set<string>());

  // Auto-check for duplicates as soon as analysis finishes for each item.
  useEffect(() => {
    rfxRows.forEach((row) => {
      const result = results[row.rfx];
      const item = items[row.rfx];
      if (
        result?.status === "done" &&
        item?.addStatus === "idle" &&
        item?.existingTenderId === undefined &&
        !checkingRef.current.has(row.rfx)
      ) {
        checkingRef.current.add(row.rfx);
        const title =
          result.connect_title ||
          result.signals?.project_name ||
          row.nazwa ||
          "";
        if (!title) {
          setItem(row.rfx, { existingTenderId: null });
          return;
        }
        // Przekazujemy URL postępowania — równa nazwa, ale różny RfX ⇒ to nie duplikat.
        api
          .checkEmlDuplicate(title, row.rfx ? connectRfxUrl(row.rfx) : "")
          .then((res) =>
            setItem(row.rfx, { existingTenderId: res.tender_id ?? null }),
          )
          .catch(() => setItem(row.rfx, { existingTenderId: null }));
      }
    });
  }, [rfxRows.length, Object.keys(results).length]);

  // ── single-item re-analysis ──────────────────────────────────────────────────
  // Restart the AI analysis for one RfX (e.g. it failed, or the signals came back poor)
  // without re-uploading the whole newsletter. Re-analyses still run STRICTLY ONE AT A TIME —
  // CONNECT's anti-bot WAF throttles on request velocity, so serialising keeps us under its
  // threshold. To spare the user the click-and-wait, clicks enqueue instead of blocking: the
  // queue drains itself one item after another (parallelism would risk tripping the WAF).
  const qc = useQueryClient();
  const reanalyze = useReanalyzeEmlRfx();
  const [reanalyzeQueue, setReanalyzeQueue] = useState<string[]>([]); // rfx waiting to run
  const [activeRfx, setActiveRfx] = useState<string | null>(null); // rfx currently running
  const [reanalyzeTaskId, setReanalyzeTaskId] = useState<string | null>(null);
  const { data: reanalyzeTask } = useTaskPolling(reanalyzeTaskId);

  // Toggle an RfX in the queue: first click enqueues, another click (while still waiting) removes.
  const toggleReanalyze = (rfx: string) => {
    if (!taskId || activeRfx === rfx) return; // parent import must exist; can't dequeue the running one
    setReanalyzeQueue((q) => (q.includes(rfx) ? q.filter((r) => r !== rfx) : [...q, rfx]));
  };

  // Drain the queue one item at a time: when nothing is running, start the next queued RfX.
  useEffect(() => {
    if (activeRfx || reanalyzeTaskId || !taskId || reanalyzeQueue.length === 0) return;
    const rfx = reanalyzeQueue[0];
    setReanalyzeQueue((q) => q.slice(1));
    setActiveRfx(rfx);
    reanalyze.mutate(
      { taskId, rfx },
      {
        onSuccess: (res) => setReanalyzeTaskId(res.task_id),
        onError: () => setActiveRfx(null), // couldn't start → free the slot, runner picks the next
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRfx, reanalyzeTaskId, reanalyzeQueue, taskId]);

  useEffect(() => {
    const st = reanalyzeTask?.status;
    if (!reanalyzeTaskId || !st) return;
    if (!["success", "failure", "cancelled"].includes(st)) return;
    const rfx = activeRfx;
    // The re-analysis task merged the fresh result into the parent import task's result —
    // force one refetch of the (already-settled) parent task so the card re-reads it.
    if (taskId) qc.invalidateQueries({ queryKey: ["bg-task", taskId] });
    // Re-open the item so it's addable again with the refreshed analysis.
    if (rfx) setItem(rfx, { addStatus: "idle", addError: undefined });
    setReanalyzeTaskId(null);
    setActiveRfx(null); // free the slot → the drain effect starts the next queued item
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reanalyzeTask?.status]);

  // Zakres z wykonawstwem (W / P+W) nie należy do portfela ZE — backend i tak odrzuci
  // takie potwierdzenie, więc wiersz nie jest „do dodania" ani pojedynczo, ani zbiorczo.
  const canAdd = (row: EmlRfxRow) =>
    !!globalClient &&
    results[row.rfx]?.status === "done" &&
    !isConstructionScope(results[row.rfx]) &&
    !items[row.rfx]?.existingTenderId &&
    items[row.rfx]?.addStatus === "idle";

  const addOne = async (row: EmlRfxRow) => {
    const item = items[row.rfx];
    if (!item || !canAdd(row)) return;
    const deadline = parseEmlDeadline(row.deadline);
    if (!deadline) return;
    setItem(row.rfx, { addStatus: "adding" });
    try {
      const rfxResult = results[row.rfx];
      const res = await api.confirmEmlImport({
        title: item.title.trim() || row.nazwa || "Przetarg ZE",
        client: globalClient,
        operator_name: row.oddzial ?? undefined,
        offer_deadline: new Date(deadline).toISOString(),
        reference_number: row.numer ?? undefined,
        rfx_row: row as object,
        parcels: (rfxResult?.parcels ?? []) as object[],
        signals: (rfxResult?.signals ?? {}) as object,
        confidence: rfxResult?.confidence ?? "",
      });
      setItem(row.rfx, { addStatus: "done", tenderId: res.tender_id });
    } catch (e: unknown) {
      // 409 Conflict = duplicate title — extract tender_id from error response
      const axiosErr = e as {
        response?: {
          status?: number;
          data?: { errors?: { detail?: { tender_id?: string } } };
        };
      };
      if (axiosErr?.response?.status === 409) {
        const duplicateId = axiosErr.response?.data?.errors?.detail?.tender_id;
        setItem(row.rfx, {
          addStatus: "error",
          addError: `duplicate:${duplicateId ?? ""}`,
        });
      } else {
        const msg =
          axiosErr?.response?.data?.errors?.detail?.detail ??
          (e instanceof Error ? e.message : "Błąd — spróbuj ponownie");
        setItem(row.rfx, { addStatus: "error", addError: msg });
      }
    }
  };

  // Rows still shown in the list (dismissed ones are hidden everywhere — list,
  // counters, and bulk-add actions).
  const visibleRows = rfxRows.filter((row) => !dismissed.has(row.rfx));

  const addableRows = visibleRows.filter(canAdd);

  // Rows that are addable AND have confirmed qualifications (see isQualified):
  // analysis succeeded with at least one detected RKW and no missing
  // qualifications/agreements. Rows with no detected RKW, a failed analysis, or
  // before the capabilities registry has loaded are excluded.
  const qualifiedRows = addableRows.filter((row) =>
    isQualified(results[row.rfx]?.signals, row.oddzial ?? null, capabilities),
  );

  // Rows whose AI analysis never completed (hard error — e.g. AI credits ran out —
  // or no result at all once the import stopped running, e.g. it was interrupted by
  // a deploy or cancelled). "no_documents" is a legitimate terminal state (nothing
  // to analyze), so it's not counted here.
  const failedAnalysisRows = visibleRows.filter((row) => {
    const st = results[row.rfx]?.status;
    return (
      (st === "error" || (!isRunning && !results[row.rfx])) &&
      items[row.rfx]?.addStatus === "idle"
    );
  });
  // Rows the interrupted import never got to at all (no per-item result).
  const missingAnalysisCount = visibleRows.filter(
    (row) => !results[row.rfx],
  ).length;
  const retryableFailedRows = failedAnalysisRows.filter(
    (row) => activeRfx !== row.rfx && !reanalyzeQueue.includes(row.rfx),
  );

  const retryAllFailed = () => {
    if (!taskId || isRunning) return;
    setReanalyzeQueue((q) => [...q, ...retryableFailedRows.map((r) => r.rfx)]);
  };

  const addAll = async () => {
    for (const row of addableRows) {
      await addOne(row);
    }
  };

  const addAllQualified = async () => {
    for (const row of qualifiedRows) {
      await addOne(row);
    }
  };

  const handleFile = (file: File) => {
    startImport.mutate(file, {
      onSuccess: (res) => setTaskId(res.task_id),
    });
  };

  const addedCount = Object.values(items).filter(
    (i) => i.addStatus === "done",
  ).length;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      <PageHeader
        title="Import przetargów ENERGA OPERATOR z EML"
        subtitle="Wgraj newsletter CONNECT — system pobierze dane i przeanalizuje dokumenty automatycznie · DLC_01"
        back="/ze-tenders"
      />

      {/* step 1 — upload */}
      {!taskId && (
        <div className="space-y-3">
          {startImport.isError && (
            <p className="text-xs text-[var(--color-primary)] font-mono">
              Nie udało się wgrać pliku EML.
            </p>
          )}
          {startImport.isPending ? (
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
              <Spinner /> Wysyłam plik…
            </div>
          ) : (
            <DropZone onFile={handleFile} />
          )}
          <p className="text-xs text-[var(--color-text-tertiary)]">
            System automatycznie pobierze dane z portalu CONNECT (bez logowania)
            i przeanalizuje każdy przetarg przez AI. Może to potrwać kilka
            minut.
          </p>
        </div>
      )}

      {/* step 2 — task running */}
      {taskId && (isRunning || (!isDone && !isFailed)) && (
        <TaskProgress
          log={task?.log ?? ""}
          onCancel={() =>
            cancelTask.mutate(undefined, { onSuccess: () => setTaskId(null) })
          }
          cancelling={cancelTask.isPending}
        />
      )}

      {/* A failed/cancelled import with NO partial results — nothing to salvage. With
          partials the list below stays visible and the interrupted-import banner offers
          finishing the analysis instead. */}
      {isFailed && rfxRows.length === 0 && (
        <div className="card p-4 flex items-center gap-2 text-sm text-red-500">
          <AlertCircle size={16} />
          Analiza nie powiodła się.{" "}
          <button className="underline" onClick={() => setTaskId(null)}>
            Spróbuj ponownie
          </button>
        </div>
      )}

      {/* step 3 — results */}
      {isDone && rfxRows.length === 0 && (
        <div className="card p-8 text-center text-sm text-[var(--color-text-tertiary)]">
          Nie znaleziono przetargów w pliku EML.{" "}
          <button className="underline" onClick={() => setTaskId(null)}>
            Wgraj inny plik
          </button>
        </div>
      )}

      {(isDone || isRunning || isFailed) && rfxRows.length > 0 && (
        <div className="space-y-4">
          {/* interrupted import — some tenders were never analysed (old single-task
              imports that hit their time budget, imports killed by a deploy, manual
              cancels). Offer finishing them in place instead of re-uploading the EML. */}
          {!isRunning && missingAnalysisCount > 0 && (
            <div className="card p-4 flex flex-wrap items-center gap-3 text-sm">
              <AlertCircle size={16} className="text-amber-500 shrink-0" />
              <span className="flex-1 min-w-[240px]">
                Import został przerwany — przeanalizowano{" "}
                {visibleRows.length - missingAnalysisCount} z {visibleRows.length}{" "}
                przetargów. Resztę dokończysz bez ponownego wgrywania pliku (kolejka
                leci po kolei — zostaw tę kartę otwartą).
              </span>
              <Button
                size="sm"
                disabled={retryableFailedRows.length === 0}
                onClick={retryAllFailed}
              >
                <RefreshCw size={13} className="mr-1" />
                {retryableFailedRows.length === 0
                  ? "Analiza w kolejce…"
                  : `Dokończ analizę (${retryableFailedRows.length})`}
              </Button>
            </div>
          )}
          {/* controls bar */}
          <div className="card p-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="block">
                <span className="section-label text-[10px]">
                  Klient (dla wszystkich)
                </span>
                <select
                  className="input-base mt-1"
                  value={globalClient}
                  onChange={(e) => setGlobalClient(e.target.value)}
                >
                  <option value="">— wybierz klienta —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-3">
              {!isRunning && failedAnalysisRows.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={retryableFailedRows.length === 0}
                  onClick={retryAllFailed}
                  title="Dodaje do kolejki ponownej analizy AI tylko przetargi, których analiza się nie powiodła (np. skończyły się kredyty). Kolejka leci po kolei."
                >
                  <RefreshCw size={13} className="mr-1" />
                  {retryableFailedRows.length === 0
                    ? "Nieudane analizy w kolejce…"
                    : `Spróbuj ponownie wykonać analizę AI (${retryableFailedRows.length})`}
                </Button>
              )}
              {addedCount > 0 && (
                <span className="text-xs text-emerald-600 font-mono">
                  {addedCount} / {visibleRows.length} dodanych
                </span>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={!globalClient || qualifiedRows.length === 0}
                onClick={addAllQualified}
                title="Dodaje tylko przetargi z potwierdzonymi uprawnieniami (RKW OK i porozumienie z oddziałem). Pomija te bez wykrytych uprawnień lub z nieudaną analizą."
              >
                Dodaj wszystkie z uprawnieniami ({qualifiedRows.length})
              </Button>
              <Button
                size="sm"
                disabled={!globalClient || addableRows.length === 0}
                onClick={addAll}
              >
                Dodaj wszystkie ({addableRows.length})
              </Button>
              <button
                className="text-xs text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)] underline"
                onClick={() => setTaskId(null)}
              >
                Wgraj inny
              </button>
            </div>
          </div>

          {/* rfx cards */}
          {visibleRows.map((row) => {
            const result = results[row.rfx];
            const item = items[row.rfx];
            if (!item) return null;

            const isError = result?.status === "error";
            const isNoDocuments = result?.status === "no_documents";
            const analyzeDone = result?.status === "done";
            // A re-analysis can be restarted once the parent import has finished and the item
            // reached a terminal analysis state — including a plain "done" with poor signals.
            const isReanalyzing = activeRfx === row.rfx && !!reanalyzeTaskId;
            // Queued = waiting in line, or picked as active but its task hasn't started yet.
            const isQueued =
              reanalyzeQueue.includes(row.rfx) || (activeRfx === row.rfx && !reanalyzeTaskId);
            const canReanalyze =
              !isRunning &&
              item.addStatus === "idle" &&
              (analyzeDone || isError || isNoDocuments || !result);
            const rowQualifies = isQualified(
              result?.signals,
              row.oddzial ?? null,
              capabilities,
            );
            // Przetarg z wykonawstwem (prefiks "P+W …" albo zakres z analizy AI) — poza
            // portfelem ZE, więc zamiast „Dodaj" pokazujemy powód i zostaje samo „Usuń".
            const rowIsConstruction = isConstructionScope(result);

            return (
              <div
                key={row.rfx}
                className={`card overflow-hidden relative ${
                  rowQualifies ? "ring-1 ring-emerald-300 ring-inset" : ""
                }`}
                title={
                  rowQualifies
                    ? "Spełnia kryteria uprawnień (RKW OK i porozumienie z oddziałem)"
                    : undefined
                }
              >
                <div className="px-4 py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    {/* title — inline editable */}
                    {item.editingTitle ? (
                      <input
                        autoFocus
                        className="input-base text-sm font-medium w-full"
                        value={item.title}
                        onChange={(e) =>
                          setItem(row.rfx, { title: e.target.value })
                        }
                        onBlur={() => setItem(row.rfx, { editingTitle: false })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Escape")
                            setItem(row.rfx, { editingTitle: false });
                        }}
                      />
                    ) : (
                      <div className="flex items-center gap-1.5 group">
                        <span className="font-medium text-sm text-[var(--color-text-primary)] break-words">
                          {item.title || row.nazwa || "—"}
                        </span>
                        {item.addStatus === "idle" && (
                          <button
                            onClick={() =>
                              setItem(row.rfx, { editingTitle: true })
                            }
                            className="opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity"
                          >
                            <PencilLine
                              size={12}
                              className="text-[var(--color-text-tertiary)]"
                            />
                          </button>
                        )}
                      </div>
                    )}

                    {/* meta row */}
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-[var(--color-text-tertiary)] font-mono flex-wrap">
                      <span>RFX {row.rfx}</span>
                      {row.numer && (
                        <a
                          href={connectRfxUrl(row.rfx)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-0.5"
                          title="Otwórz w portalu CONNECT"
                        >
                          {row.numer}
                          <ExternalLink size={10} />
                        </a>
                      )}
                      {row.oddzial && <span>{row.oddzial}</span>}
                      {row.deadline && <span>Termin: {formatDateTime(parseEmlDeadline(row.deadline), row.deadline)}</span>}
                    </div>

                    {/* AI summary */}
                    {analyzeDone && (
                      <AiSummary
                        result={result}
                        warnings={computeWarnings(
                          result.signals,
                          row.oddzial ?? null,
                          capabilities,
                        )}
                      />
                    )}
                    {isNoDocuments && (
                      <p className="mt-1 text-[11px] text-amber-600 font-mono">
                        Brak plików na portalu CONNECT — przetarg nie zawiera załączników do pobrania.
                      </p>
                    )}
                    {isError && (
                      <p className="mt-1 text-[11px] text-red-500 font-mono">
                        Błąd analizy: {result.error}
                        {result.retries
                          ? ` (ponawiano ${result.retries} ${
                              result.retries === 1 ? "raz" : "razy"
                            })`
                          : ""}
                      </p>
                    )}
                    {!result && isRunning && (
                      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-[var(--color-text-tertiary)]">
                        <Loader2 size={10} className="animate-spin" />
                        Analiza w toku…
                      </div>
                    )}
                    {/* The import stopped without ever analysing this row — say so
                        instead of showing an eternal spinner. */}
                    {!result && !isRunning && !isQueued && !isReanalyzing && (
                      <p className="mt-1 text-[11px] text-amber-600 font-mono">
                        Nieprzeanalizowany — import został przerwany.
                      </p>
                    )}

                    {/* restart AI analysis for this single RfX */}
                    {isReanalyzing ? (
                      <div className="mt-2 space-y-1">
                        <div className="inline-flex items-center gap-1.5 text-[11px] text-violet-500">
                          <Loader2 size={11} className="animate-spin" />
                          Ponawiam analizę AI…
                        </div>
                        {reanalyzeTask?.log && (
                          <p className="text-[10px] font-mono text-[var(--color-text-tertiary)] truncate">
                            {reanalyzeTask.log
                              .trim()
                              .split("\n")
                              .filter(Boolean)
                              .slice(-1)[0]}
                          </p>
                        )}
                      </div>
                    ) : isQueued ? (
                      <button
                        onClick={() => toggleReanalyze(row.rfx)}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] text-violet-400 hover:text-red-500 transition-colors"
                        title="W kolejce do analizy AI — kliknij, aby usunąć z kolejki"
                      >
                        <Clock size={11} />
                        W kolejce{reanalyzeQueue.indexOf(row.rfx) >= 0 ? ` (${reanalyzeQueue.indexOf(row.rfx) + 1})` : ""} — usuń
                      </button>
                    ) : (
                      canReanalyze && (
                        <button
                          onClick={() => toggleReanalyze(row.rfx)}
                          className="mt-2 inline-flex items-center gap-1 text-[11px] text-[var(--color-text-tertiary)] hover:text-violet-500 transition-colors"
                          title="Dodaj do kolejki analizy AI (pobierze dokumenty z CONNECT i uruchomi analizę; kolejka leci po kolei)"
                        >
                          <RefreshCw size={11} />
                          Ponów analizę AI
                        </button>
                      )
                    )}
                  </div>

                  {/* action */}
                  <div className="shrink-0 flex flex-col items-end gap-1">
                    {item.addStatus === "idle" &&
                      analyzeDone &&
                      item.existingTenderId && (
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="flex items-center gap-1 text-xs text-amber-600">
                            <AlertCircle size={12} />
                            Duplikat
                          </span>
                          <Link
                            to={`/ze-tenders/${item.existingTenderId}`}
                            className="text-[10px] underline text-[var(--color-text-tertiary)]"
                          >
                            Otwórz istniejący →
                          </Link>
                        </div>
                      )}
                    {item.addStatus === "idle" &&
                      analyzeDone &&
                      !item.existingTenderId &&
                      rowIsConstruction && (
                        <div className="flex items-center gap-1">
                          <span
                            className="flex items-center gap-1 text-xs text-amber-600"
                            title="Portfel ZE prowadzi tylko projekty — przetarg z robotami budowlanymi nie zostanie utworzony"
                          >
                            <AlertCircle size={12} />
                            {SCOPE_LABEL[effectiveScope(result)] ?? "Wykonawstwo"} —
                            poza ZE
                          </span>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => dismissRow(row.rfx)}
                            title="Usuń z listy — przetarg nie zostanie utworzony"
                          >
                            Usuń
                          </Button>
                        </div>
                      )}
                    {item.addStatus === "idle" &&
                      analyzeDone &&
                      !item.existingTenderId &&
                      !rowIsConstruction && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!globalClient}
                            onClick={() => addOne(row)}
                            className={
                              rowQualifies
                                ? "ring-0 ring-emerald-500 border-emerald-500 text-emerald-700 dark:text-emerald-400"
                                : undefined
                            }
                          >
                            Dodaj
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => dismissRow(row.rfx)}
                            title="Usuń z listy — przetarg nie zostanie utworzony"
                          >
                            Usuń
                          </Button>
                        </div>
                      )}
                    {item.addStatus === "adding" && (
                      <span className="flex items-center gap-1 text-xs text-[var(--color-text-tertiary)]">
                        <Loader2 size={12} className="animate-spin" />
                        Dodaję…
                      </span>
                    )}
                    {item.addStatus === "done" && item.tenderId && (
                      <Link
                        to={`/ze-tenders/${item.tenderId}`}
                        className="flex items-center gap-1 text-xs text-emerald-600 hover:underline"
                      >
                        <CheckCircle2 size={13} />
                        Dodano →
                      </Link>
                    )}
                    {item.addStatus === "error" &&
                      item.addError?.startsWith("duplicate:") && (
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="flex items-center gap-1 text-xs text-amber-600">
                            <AlertCircle size={12} />
                            Duplikat
                          </span>
                          {item.addError.slice(10) && (
                            <Link
                              to={`/ze-tenders/${item.addError.slice(10)}`}
                              className="text-[10px] underline text-[var(--color-text-tertiary)]"
                            >
                              Otwórz istniejący →
                            </Link>
                          )}
                        </div>
                      )}
                    {item.addStatus === "error" &&
                      !item.addError?.startsWith("duplicate:") && (
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="flex items-center gap-1 text-xs text-red-500">
                            <AlertCircle size={12} />
                            Błąd
                          </span>
                          <button
                            className="text-[10px] underline text-[var(--color-text-tertiary)]"
                            onClick={() =>
                              setItem(row.rfx, {
                                addStatus: "idle",
                                addError: undefined,
                              })
                            }
                          >
                            Spróbuj ponownie
                          </button>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
